import { getTransformedData } from "@/tools/analysis-saving-utils";
import { clearStore, ColorString } from "@faro-lotv/app-component-toolbox";
import { assert } from "@faro-lotv/foundation";
import { GUID } from "@faro-lotv/ielement-types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Matrix4, Matrix4Tuple, Vector3Tuple } from "three";

/** The type of reference plane used for an analysis */
export enum ReferencePlaneType {
  /** Reference plane is computed by best-fit of the selected points */
  flatness = "flatness",

  /** Reference plane is set to a level plane at a fixed elevation */
  elevation = "elevation",

  /** Reference plane is set to a level plane at elevation of the bestfit center */
  levelness = "levelness",

  /** Reference plane is constrained to vertical */
  plumbness = "plumbness",

  /** Volume analysis */
  volume = "volume",
}

/** Colormap defined as a list of value and color pairs  */
export type PointCloudAnalysisColormap = Array<{
  /** Scaled value within range of [0, 1] */
  value: number;
  /** Color associated with the value */
  color: ColorString;
}>;

export type PointCloudAnalysisPlane = {
  /** The normal of the plane */
  normal: Vector3Tuple;
  /** A point on the plane */
  point: Vector3Tuple;
};

type PointCloudAnalysisPlanes = {
  /** The plane fitted to the analysis points */
  fittedPlane: PointCloudAnalysisPlane | undefined;
  /** The vertical plane fitted to the analysis points */
  fittedPlumbPlane: PointCloudAnalysisPlane | undefined;
};

export type FittedPlanesMap = Record<number, PointCloudAnalysisPlanes>;

/** Label associated with a point cloud analysis */
export type PointCloudAnalysisLabel = {
  /** The unique id of this label */
  id: GUID;

  /** The position of the label */
  position: Vector3Tuple;
};

/** The type of base plane used for volume analysis */
export enum VolumeAnalysisBasePlaneType {
  /** Best-fit plane of the selected polygon */
  bestFitPolygonBase = "bestFitPolygonBase",

  /** Level plane at the lowest point of the point cloud */
  levelLowestPointBase = "levelLowestPointBase",

  /** Level plane at the highest point of the point cloud */
  levelHighestPointBase = "levelHighestPointBase",

  /** Level plane at a specific elevation */
  levelAtElevation = "levelAtElevation",
}

/** Volume analysis result data */
export type PointCloudVolumeAnalysisResult = {
  /** The origin point of the base plane for the volume analysis */
  basePlaneOrigin: Vector3Tuple;

  /** The normal vector of the base plane for the volume analysis */
  basePlaneNormal: Vector3Tuple;

  /** The base area of the volume */
  baseArea: number;

  /** The estimated volume value */
  volume: number;

  /** The minimum height of the volume */
  heightMin: number;

  /** The maximum height of the volume */
  heightMax: number;
};

export type PointCloudAnalysis = {
  /** The unique id of this analysis */
  id: GUID;

  /** The polygon used to select points for analysis */
  polygonSelection: Vector3Tuple[];

  /** Tolerance (+/-) value for the analysis */
  tolerance: number;

  /** Search distance (+/-) from the reference plane, used to collect analysis points */
  searchDistance: number;

  /** The id of the point cloud iElement this analysis originated from */
  parentId: GUID;

  /** The type of reference plane used for this analysis */
  referencePlaneType: ReferencePlaneType;

  /** Colormap used to colorize point according to its deviation to the reference plane */
  colormap: PointCloudAnalysisColormap;

  /** The elevation for the level reference plane */
  elevation: number;

  /** Show reference plane when analysis is active */
  showReferencePlane?: boolean;

  /** if true analysis is visible */
  isVisible: boolean;

  /** Fitted plane from the user selected point cloud region */
  fittedPlane?: PointCloudAnalysisPlane;

  /** Fitted plumb plane from user selected point cloud region */
  fittedPlumbPlane?: PointCloudAnalysisPlane;

  /** A cache that remembers which fitted planes resulted from a given search distance. */
  fittedPlanesMap: FittedPlanesMap;

  /** if true, the analysis is edited since last save */
  isDirty: boolean;

  /** The labels associated with this analysis */
  labels: PointCloudAnalysisLabel[];

  /** The type of base plane used for the volume analysis */
  volumeBasePlaneType?: VolumeAnalysisBasePlaneType;

  /** The elevation used for the VolumeAnalysisBasePlaneType.levelAtElevation type base plane*/
  volumeBasePlaneElevation?: number;

  /** Number of isolines to display on the volume mesh */
  volumeDisplayNbIsoLines?: number;

  /** Distance to offset the volume mesh above the reference plane */
  volumeDisplayOffset?: number;

  /** Volume analysis result data */
  volumeAnalysisResult?: PointCloudVolumeAnalysisResult;
};

export type PointCloudAnalysisToolState = {
  /** For each specific point cloud, identified by its GUID, store the list of analyses associated to it */
  analyses: Record<GUID, PointCloudAnalysis[]>;

  /** The active analysis id or undefined if none*/
  activeAnalysisId?: GUID;

  /** If true, the active analysis should be shown */
  showActiveAnalysis?: boolean;

  /**
   * The last created analysis id; Used to keep toolbar expanded when a new analysis is created
   * This is used to ensure that the toolbar remains expanded when a new analysis is created,
   */
  lastCreatedAnalysisId?: GUID;

  /** Number of analysis created this session */
  numberOfSessionAnalyses: number;
};

const initialState: PointCloudAnalysisToolState = {
  analyses: {},
  activeAnalysisId: undefined,
  numberOfSessionAnalyses: 0,
  showActiveAnalysis: false,
};

/**
 * Set the visibility of the analysis with the given id.
 *
 * @param analyses list of analyses
 * @param analysisId if of te analysis to set the visibility
 * @param visible new analysis's visibility
 */
function setAnalysisVisibilityInternal(
  analyses: Record<GUID, PointCloudAnalysis[]>,
  analysisId: GUID,
  visible: boolean,
): void {
  const analysis = findAnalysis(analyses, analysisId);
  if (analysis && analysis.isVisible !== visible) {
    analysis.isVisible = visible;
  }
}

const pointCloudAnalysisToolSlice = createSlice({
  name: "pointCloudAnalysisTool",
  initialState,
  reducers: {
    addAnalysis(
      state,
      action: PayloadAction<{
        pointCloudID: GUID;
        analysis: PointCloudAnalysis;
      }>,
    ) {
      state.numberOfSessionAnalyses++;
      if (!(action.payload.pointCloudID in state.analyses)) {
        state.analyses[action.payload.pointCloudID] = [];
      }
      // Check if the analysis with same ID already exists in the parent cloud, if so remove it
      const analysisId = action.payload.analysis.id;
      const analysis = findAnalysis(state.analyses, analysisId);
      if (analysis) {
        state.analyses[analysis.parentId] = state.analyses[
          analysis.parentId
        ].filter((a) => a.id !== analysisId);
      }

      assert(
        !state.analyses[action.payload.pointCloudID].some(
          (analysis) => analysis.id === action.payload.analysis.id,
        ),
        "Not supposed to add 2 analysis with same id",
      );
      state.analyses[action.payload.pointCloudID].push(action.payload.analysis);
    },

    removeAnalysis(state, action: PayloadAction<GUID>) {
      const analysis = findAnalysis(state.analyses, action.payload);
      if (analysis) {
        if (state.activeAnalysisId === analysis.id) {
          state.activeAnalysisId = undefined;
        }
        const analyses = state.analyses[analysis.parentId];
        state.analyses[analysis.parentId] = analyses.filter(
          (a) => a.id !== action.payload,
        );
      }
    },

    setAnalysisTolerance(
      state,
      action: PayloadAction<{ analysisId: GUID; tolerance: number }>,
    ) {
      const analysis = findAnalysis(state.analyses, action.payload.analysisId);
      if (analysis && analysis.tolerance !== action.payload.tolerance) {
        analysis.tolerance = action.payload.tolerance;
        analysis.isDirty = true;
      }
    },

    setAnalysisSearchDistanceAndFittedPlanes(
      state,
      action: PayloadAction<{
        analysisId: GUID;
        searchDistance: number;
        fittedPlane: PointCloudAnalysisPlane | undefined;
        fittedPlumbPlane: PointCloudAnalysisPlane | undefined;
      }>,
    ) {
      const analysis = findAnalysis(state.analyses, action.payload.analysisId);
      if (
        analysis &&
        analysis.searchDistance !== action.payload.searchDistance
      ) {
        analysis.searchDistance = action.payload.searchDistance;
        analysis.fittedPlane = action.payload.fittedPlane;
        analysis.fittedPlumbPlane = action.payload.fittedPlumbPlane;
        // Remember in the 'fittedPlanesMap' that this search distance resulted in these fitted planes.
        if (action.payload.fittedPlane && action.payload.fittedPlumbPlane) {
          analysis.fittedPlanesMap[action.payload.searchDistance] = {
            fittedPlane: action.payload.fittedPlane,
            fittedPlumbPlane: action.payload.fittedPlumbPlane,
          };
        }
        analysis.isDirty = true;
      }
    },

    setActiveAnalysisId(state, action: PayloadAction<GUID | undefined>) {
      state.activeAnalysisId = action.payload;
    },

    setLastCreatedAnalysisId(state, action: PayloadAction<GUID | undefined>) {
      state.lastCreatedAnalysisId = action.payload;
    },

    setAnalysisReferencePlaneType(
      state,
      action: PayloadAction<{
        analysisId: GUID;
        referencePlaneType: ReferencePlaneType;
      }>,
    ) {
      const analysis = findAnalysis(state.analyses, action.payload.analysisId);
      if (
        analysis &&
        analysis.referencePlaneType !== action.payload.referencePlaneType
      ) {
        analysis.referencePlaneType = action.payload.referencePlaneType;
        analysis.isDirty = true;
      }
    },

    setAnalysisColormap(
      state,
      action: PayloadAction<{
        analysisId: GUID;
        colormap: PointCloudAnalysisColormap;
      }>,
    ) {
      const analysis = findAnalysis(state.analyses, action.payload.analysisId);
      if (analysis) {
        analysis.colormap = action.payload.colormap;
        analysis.isDirty = true;
      }
    },

    setAnalysisElevation(
      state,
      action: PayloadAction<{ analysisId: GUID; elevation: number }>,
    ) {
      const analysis = findAnalysis(state.analyses, action.payload.analysisId);
      if (analysis && analysis.elevation !== action.payload.elevation) {
        analysis.elevation = action.payload.elevation;
        analysis.isDirty = true;
      }
    },

    setAnalysisShowReferencePlane(
      state,
      action: PayloadAction<{ analysisId: GUID; showReferencePlane: boolean }>,
    ) {
      const analysis = findAnalysis(state.analyses, action.payload.analysisId);
      if (
        analysis &&
        analysis.showReferencePlane !== action.payload.showReferencePlane
      ) {
        analysis.showReferencePlane = action.payload.showReferencePlane;
        analysis.isDirty = true;
      }
    },

    setAnalysisVisibility(
      state,
      action: PayloadAction<{
        analysisId: GUID;
        visible: boolean;
      }>,
    ) {
      setAnalysisVisibilityInternal(
        state.analyses,
        action.payload.analysisId,
        action.payload.visible,
      );
    },

    setActiveAnalysisVisibility(
      state,
      action: PayloadAction<{ visible: boolean }>,
    ) {
      if (state.activeAnalysisId) {
        setAnalysisVisibilityInternal(
          state.analyses,
          state.activeAnalysisId,
          action.payload.visible,
        );
      }
    },

    setAnalysisDirtyFlag(
      state,
      action: PayloadAction<{ analysisId: GUID; dirty: boolean }>,
    ) {
      const analysis = findAnalysis(state.analyses, action.payload.analysisId);
      if (analysis) {
        analysis.isDirty = action.payload.dirty;
      }
    },

    addAnalysisLabel(
      state,
      action: PayloadAction<{
        analysisId: GUID;
        label: PointCloudAnalysisLabel;
      }>,
    ) {
      const analysis = findAnalysis(state.analyses, action.payload.analysisId);
      if (analysis) {
        analysis.labels.push(action.payload.label);
        analysis.isDirty = true;
      }
    },

    removeAnalysisLabel(
      state,
      action: PayloadAction<{
        analysisId: GUID;
        labelId: GUID;
      }>,
    ) {
      const analysis = findAnalysis(state.analyses, action.payload.analysisId);
      if (analysis) {
        const { labels } = analysis;
        analysis.labels = labels.filter(
          (label) => label.id !== action.payload.labelId,
        );
        analysis.isDirty = true;
      }
    },

    alignAnalysis(
      state,
      action: PayloadAction<{
        parentCloudId: GUID;
        alignMatrix: Matrix4Tuple;
      }>,
    ) {
      const analyses =
        action.payload.parentCloudId in state.analyses
          ? state.analyses[action.payload.parentCloudId]
          : [];
      const alignMatrix = new Matrix4().fromArray(action.payload.alignMatrix);
      const alignedAnalyses = analyses.map((analysis) => {
        const transformedData = getTransformedData(
          alignMatrix,
          analysis.polygonSelection,
          analysis.labels,
          analysis.fittedPlane,
          analysis.fittedPlumbPlane,
        );
        return {
          ...analysis,
          polygonSelection: transformedData.polygon,
          labels: transformedData.labels,
          fittedPlane: transformedData.fittedPlane,
          fittedPlumbPlane: transformedData.fittedPlumbPlane,
          fittedPlanesMap: {},
        };
      });
      if (alignedAnalyses.length) {
        state.analyses[action.payload.parentCloudId] = alignedAnalyses;
      }
    },

    setShowActiveAnalysis(state, action: PayloadAction<boolean>) {
      state.showActiveAnalysis = action.payload;
    },

    setVolumeAnalysisResult(
      state,
      action: PayloadAction<{
        analysisId: GUID;
        volumeAnalysisResult: PointCloudVolumeAnalysisResult | undefined;
      }>,
    ) {
      const analysis = findAnalysis(state.analyses, action.payload.analysisId);
      if (analysis) {
        analysis.volumeAnalysisResult = action.payload.volumeAnalysisResult;
        analysis.isDirty = true;
      }
    },

    setAnalysisVolumeBasePlaneType(
      state,
      action: PayloadAction<{
        analysisId: GUID;
        volumeBasePlaneType: VolumeAnalysisBasePlaneType;
      }>,
    ) {
      const analysis = findAnalysis(state.analyses, action.payload.analysisId);
      if (
        analysis &&
        analysis.volumeBasePlaneType !== action.payload.volumeBasePlaneType
      ) {
        analysis.volumeBasePlaneType = action.payload.volumeBasePlaneType;
        analysis.isDirty = true;
      }
    },

    setAnalysisVolumeDisplayIsoLines(
      state,
      action: PayloadAction<{
        analysisId: GUID;
        nbIsoLines: number;
      }>,
    ) {
      const analysis = findAnalysis(state.analyses, action.payload.analysisId);
      if (
        analysis &&
        action.payload.nbIsoLines >= 0 &&
        analysis.volumeDisplayNbIsoLines !== action.payload.nbIsoLines
      ) {
        analysis.volumeDisplayNbIsoLines = action.payload.nbIsoLines;
        analysis.isDirty = true;
      }
    },

    setAnalysisVolumeDisplayOffset(
      state,
      action: PayloadAction<{
        analysisId: GUID;
        offset: number | undefined;
      }>,
    ) {
      const analysis = findAnalysis(state.analyses, action.payload.analysisId);
      if (analysis && analysis.volumeDisplayOffset !== action.payload.offset) {
        analysis.volumeDisplayOffset = action.payload.offset;
        analysis.isDirty = true;
      }
    },

    setAnalysisVolumeBasePlaneElevation(
      state,
      action: PayloadAction<{
        analysisId: GUID;
        elevation: number | undefined;
      }>,
    ) {
      const analysis = findAnalysis(state.analyses, action.payload.analysisId);
      if (
        analysis &&
        analysis.volumeBasePlaneElevation !== action.payload.elevation
      ) {
        analysis.volumeBasePlaneElevation = action.payload.elevation;
        analysis.isDirty = true;
      }
    },
  },

  extraReducers: (builder) => {
    builder.addCase(clearStore, () => initialState);
  },
});

export const pointCloudAnalysisToolReducer =
  pointCloudAnalysisToolSlice.reducer;

export const {
  addAnalysis,
  alignAnalysis,
  removeAnalysis,
  setAnalysisTolerance,
  setAnalysisSearchDistanceAndFittedPlanes,
  setActiveAnalysisId,
  setLastCreatedAnalysisId,
  setAnalysisColormap,
  setAnalysisReferencePlaneType,
  setAnalysisElevation,
  setAnalysisShowReferencePlane,
  setAnalysisVisibility,
  setActiveAnalysisVisibility,
  setAnalysisDirtyFlag,
  addAnalysisLabel,
  removeAnalysisLabel,
  setShowActiveAnalysis,
  setVolumeAnalysisResult,
  setAnalysisVolumeBasePlaneType,
  setAnalysisVolumeDisplayIsoLines,
  setAnalysisVolumeDisplayOffset,
  setAnalysisVolumeBasePlaneElevation,
} = pointCloudAnalysisToolSlice.actions;

/**
 *  @returns the analysis matching a given id
 *  @param analyses List of analyses per point cloud
 *  @param analysisId The id of the analysis to get
 */
export function findAnalysis(
  analyses: Record<GUID, PointCloudAnalysis[]>,
  analysisId: GUID,
): PointCloudAnalysis | undefined {
  for (const listOfAnalyses of Object.values(analyses)) {
    const analysis = listOfAnalyses.find((a) => a.id === analysisId);
    if (analysis) {
      return analysis;
    }
  }
}
